import { useState, type FormEvent } from "react";
import { LoaderCircle, Plus } from "lucide-react";
import type { Task, Project } from "../../../server/src/models";
import { api } from "../api";
import { ParentPicker } from "./ParentPicker";
import { Modal } from "./ui";

export function NewTask({
  project,
  lane,
  revision,
  onClose,
  onCreated,
}: {
  project: string;
  lane: string;
  revision: unknown;
  onClose: () => void;
  onCreated: (task: Task) => void;
}) {
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const f = new FormData(e.currentTarget);
    try {
      let task = await api<Task>(`/v1/projects/${project}/tasks`, "POST", {
        title: f.get("title"),
        description: f.get("description"),
        priority: f.get("priority"),
        tags: String(f.get("tags"))
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        parent: f.get("parent") || null,
        criteria: String(f.get("criteria"))
          .split("\n")
          .filter((s) => s.trim())
          .map((text) => ({ text: text.trim(), met: false })),
      });
      if (lane !== "backlog")
        task = await api<Task>(`/v1/tasks/${task.id}/move`, "POST", { lane });
      onCreated(task);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="A new place to start" onClose={onClose}>
      <form className="form-pad" onSubmit={submit}>
        <p className="form-intro">
          Keep the task concise. The context can grow as you work.
        </p>
        <label>
          Task title
          <input
            name="title"
            data-initial-focus
            required
            maxLength={300}
            placeholder="What needs to happen?"
          />
        </label>
        <label>
          Description
          <textarea
            name="description"
            rows={3}
            placeholder="The problem, the goal, and a little background."
          />
        </label>
        <div className="form-grid">
          <label>
            Priority
            <select name="priority" defaultValue="normal">
              <option value="urgent">Urgent</option>
              <option value="high">High</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </label>
          <ParentPicker project={project} revision={revision} />
        </div>
        <label>
          Tags
          <input name="tags" placeholder="api, investigation, ui" />
        </label>
        <label>
          Acceptance criteria
          <textarea
            name="criteria"
            rows={3}
            placeholder="One clear outcome per line"
          />
        </label>
        {error && (
          <div className="form-error" role="alert">
            {error}
          </div>
        )}
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary" disabled={busy}>
            {busy ? (
              <LoaderCircle size={15} className="spin" />
            ) : (
              <Plus size={15} />
            )}
            Create task
          </button>
        </div>
      </form>
    </Modal>
  );
}
export function NewProject({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (project: Project) => void;
}) {
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    const f = new FormData(e.currentTarget);
    try {
      onCreated(
        await api<Project>("/v1/projects", "POST", Object.fromEntries(f)),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="Create a project" onClose={onClose}>
      <form onSubmit={submit} className="form-pad">
        <label>
          Project name
          <input name="name" required placeholder="e.g. Booking API" />
        </label>
        <div className="form-grid">
          <label>
            Project key
            <input
              name="key"
              required
              pattern="[a-z][a-z0-9-]+"
              placeholder="booking-api"
            />
          </label>
          <label>
            Task prefix
            <input
              name="prefix"
              required
              pattern="[A-Z]+"
              placeholder="BOOK"
              maxLength={12}
            />
          </label>
        </div>
        <label>
          Base branch
          <input name="base_branch" defaultValue="main" required />
        </label>
        <p className="form-intro">
          The prefix gives each task a permanent ID, like BOOK-1.
        </p>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary" disabled={busy}>
            Create project
          </button>
        </div>
      </form>
    </Modal>
  );
}
